import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, ReplaySubject, combineLatest, Subscription} from 'rxjs';

import { APIService, KPI } from '../../@core/api';
import {map, switchMap, tap} from 'rxjs/operators';
import { SELECTED_PLAN_ID } from './constants';
import { PerimetersService } from './perimeters.service';

@Injectable()
export class WorkflowService {

  #_investmentPlans$ = new BehaviorSubject([]);

  investmentPlans$: Observable<KPI.InvestmentPlan[]> = this.#_investmentPlans$.asObservable();

  private _rounds$: ReplaySubject<KPI.WorkflowRound[]>;

  constructor(
    private api: APIService,
    private perimetersService: PerimetersService,
  ) {
    this.rounds$.next([])
  }

  public loadInvestmentPlans(
  ): Subscription {
    return this.api.get_investment_plans().pipe(
      tap(plans => this.#_investmentPlans$.next(plans))
    ).subscribe()
  }

  private _selectedPlanChanged$ = new BehaviorSubject<void>(undefined);
  public setSelectedPlan(planId: number) {
    localStorage.setItem(SELECTED_PLAN_ID, planId.toString());
    this._selectedPlanChanged$.next();
  }

  public get availableAndSelectedPlans$(): Observable<[KPI.InvestmentPlan[], KPI.InvestmentPlan]> {
    return combineLatest([
      this.api.get_investment_plans(),
      this._selectedPlanChanged$,
    ]).pipe(
      map(([res]) => {
        const activePlans = res.filter(elt => !elt.closed);
        let plans: KPI.InvestmentPlan[] = activePlans;
        let selectedPlan: KPI.InvestmentPlan;
        const storedPlan = parseInt(localStorage.getItem(SELECTED_PLAN_ID))
        if (storedPlan && plans) {
          selectedPlan = plans.find(plan => plan.id === storedPlan);
        } else {
          if (activePlans.length > 0) {
            selectedPlan = activePlans.find(plan => plan.year === new Date().getFullYear()) || activePlans[0];
          } else if (plans.length) {
            plans = plans;
            selectedPlan = plans[plans.length - 1];
          }
        }
        return [plans, selectedPlan];
      }),
    )
  }

  public get availablePlans$(): Observable<KPI.InvestmentPlan[]> {
    return this.availableAndSelectedPlans$.pipe(
      map((res: [KPI.InvestmentPlan[], KPI.InvestmentPlan]) => res[0]),
    )
  }

  public get selectedPlan$(): Observable<KPI.InvestmentPlan> {
    return this.availableAndSelectedPlans$.pipe(
      map((res: [KPI.InvestmentPlan[], KPI.InvestmentPlan]) => res[1]),
    )
  }

  public loadWorkflowSteps(
  ): Observable<KPI.WorkflowStep[]> {
    return this.api.get_workflow_steps()
  }

  public get waitingSites$(): Observable<KPI.WorkflowSite[]> {
    return combineLatest([
      this.selectedPlan$,
      this.perimetersService.perimeters$,
    ]).pipe(
      switchMap(([selectedPlan, perimeters]) => this.api.get_workflow_waiting_sites(selectedPlan, perimeters))
    )
  }

  public createRound(
    plan: KPI.InvestmentPlan,
    name: string,
    perimeters: KPI.Perimeter[],
    steps: KPI.WorkflowStep[],
    selectedTagIds: number[],
    projectCode?: string,
  ): Observable<KPI.WorkflowRoundCreation> {
    return this.api.create_workflow_round(plan, name, perimeters, steps, selectedTagIds, projectCode)
  }

  public getRounds(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowRound[]> {
    return new Observable<KPI.WorkflowRound[]>(
      (observer) => {
        this.api.get_workflow_rounds(plan, perimeters).subscribe(
          (rounds: KPI.WorkflowRound[]) => {
            this._rounds$.next(rounds)
            observer.next(rounds)
          },
          err => {
            observer.error(err)
          }
        )
      }
    )
  }

  public getSeparateRounds(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowRoundsListResponse> {
    return new Observable<KPI.WorkflowRoundsListResponse>(
      (observer) => {
        this.api.get_workflow_separate_rounds(plan, perimeters).subscribe(
          (response: KPI.WorkflowRoundsListResponse) => {
            const allRounds = [...response.rounds_needing_action, ...response.rounds_for_information];
            this._rounds$.next(allRounds)
            observer.next(response)
          },
          err => {
            observer.error(err)
          }
        )
      }
    )
  }

  public get rounds$(): ReplaySubject<KPI.WorkflowRound[]> {
    if (!this._rounds$) {
      this._rounds$ = new ReplaySubject(1);
    }
    return this._rounds$
  }

  public getRoundsHistory(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowRoundHistory[]> {
    return this.api.get_workflow_history(plan, perimeters)
  }

  public getRoundSites(
    round: KPI.WorkflowRound,
  ): Observable<KPI.WorkflowRoundResponse> {
    return this.api.get_workflow_round_sites(round);
  }

  public getProposedTotalFromTags$(tags: number[], sites: number[], investmentPlan: number){
    return this.api.get_workflow_proposed_total_from_tags(tags, sites, investmentPlan)
  }

  public getRoundDetail(
    round: KPI.WorkflowRoundHistory,
  ): Observable<KPI.WorkflowRoundSite[]> {
    return this.api.get_workflow_round_detail(round)
  }

  public deleteRound(
    round: KPI.WorkflowRoundHistory,
  ): Observable<KPI.WorkflowStatus> {
    return this.api.delete_workflow_round(round)
  }

  public ackSteps(
    round: KPI.WorkflowRound,
    stepId: number,
    ack_sites: number[],
    stop_sites: number[]
  ): Observable<KPI.WorkflowStatusEx> {
    return this.api.ack_workflow_steps(round, stepId, ack_sites, stop_sites)
  }

  public closeSites(
    round: KPI.WorkflowRound,
    sites: KPI.WorkflowSite[]
  ): Observable<{ round_status: KPI.WorkflowClosureStatus, status: string }> {
    return this.api.close_workflow_sites(round, sites)
  }

  public getFlashBudget(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowFlashBudget> {
    return this.api.get_workflow_flash_budget(plan, perimeters)
  }

  public getFlashInvestments(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowFlashInvestments> {
    return this.api.get_workflow_flash_investments(plan, perimeters)
  }

  public setFlashSiteBudget(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[],
    sites: KPI.WorkflowFlashSiteBudget[]
  ): Observable<KPI.WorkflowFlashBudget> {
    return this.api.set_workflow_flash_site_budget(plan, perimeters, sites)
  }

  public setFlashGlobalBudget(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[],
    budget: number
  ): Observable<KPI.WorkflowFlashBudget> {
    return this.api.set_workflow_flash_global_budget(plan, perimeters, budget)
  }

  public loadPerimeterPlans(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowPerimeterPlan[]> {
    return this.api.get_workflow_perimeter_plans(plan, perimeters)
  }

  public closePerimeterPlans(
    plan: KPI.InvestmentPlan,
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.WorkflowStatus> {
    return this.api.workflow_close_perimeter_plans(plan, perimeters)
  }

  public postComment(
    round: KPI.WorkflowRound | KPI.WorkflowRoundHistory,
    site: KPI.WorkflowSite,
    step: KPI.WorkflowStep,
    text: string
  ): Observable<KPI.WorkflowRoundStepComment> {
    return this.api.post_workflow_comment(round.id, site.id, step.id, text)
  }

  public patchComment(
    comment: KPI.WorkflowRoundStepComment,
    text: string
  ): Observable<KPI.WorkflowRoundStepComment> {
    return this.api.patch_workflow_comment(comment, text)
  }

  public deleteComment(
    comment: KPI.WorkflowRoundStepComment
  ): Observable<any> {
    return this.api.delete_workflow_comment(comment)
  }

  public getSiteTagProposedAmount(site: KPI.WorkflowSite, tagId: number): number {
    if (site.proposed_by_tag && site.proposed_by_tag[tagId]) {
      return site.proposed_by_tag[tagId].budget
    }
    return 0
  }

  public getSiteTagProposedCount(site: KPI.WorkflowSite, tagId: number): number {
    if (site.proposed_by_tag && site.proposed_by_tag[tagId]) {
      return site.proposed_by_tag[tagId].count
    }
    return 0
  }

  public getTotalTagProposedAmount(sites: KPI.WorkflowSite[], tagId: number): number {
    let total = 0;
    for (const site of sites) {
      total += this.getSiteTagProposedAmount(site, tagId)
    }
    return total
  }

  public getTotalTagProposedCount(sites: KPI.WorkflowSite[], tagId: number): number {
    let total = 0;
    for (const site of sites) {
      total += this.getSiteTagProposedCount(site, tagId)
    }
    return total
  }

  public formatNames(members: KPI.StepMember[]): string {
    const formattedNames = members.filter((obj1, i, arr) =>
      arr.findIndex(obj2 => JSON.stringify(obj1) === JSON.stringify(obj2)) === i
    ).map(member => {
      const firstNameInitial = member.first_name.charAt(0);
      return `${firstNameInitial}. ${member.last_name}`;
    });

    if (formattedNames.length === 1) {
      return formattedNames[0];
    } else if (formattedNames.length === 2) {
      return formattedNames.join(' & ');
    } else if (formattedNames.length >= 3) {
      return `${formattedNames.slice(0, -1).join(', ')} & ${formattedNames.slice(-1)}`;
    }
  }
}
